import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PencilIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import InfoModal from './common/InfoModal';

export interface Tab {
  id: string;
  label: string;
  href?: string;
  current?: boolean;
  icon?: React.ReactNode;
  /** A node lets a caller emphasise its own counts without restyling every bar. */
  badge?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;  // Manager-required workspace, highlighted in blue
  accessKey?: string;  // Alt+letter shortcut to activate this tab
}

interface TabBarProps {
  tabs: Tab[];
  onTabChange?: (tabId: string) => void;
  onAddTab?: () => void;  // Callback when + button is clicked to add a new tab
  onEditTab?: (tabId: string, newName: string) => void;  // Callback when tab is renamed
  onDeleteTab?: (tabId: string) => void;  // Callback when tab is deleted
  onReorderTabs?: (reorderedTabs: Tab[]) => void;  // Callback when tabs are reordered via drag-and-drop
  maxTabs?: number;  // Maximum number of tabs allowed (default 9)
  protectedTabIds?: string[];  // Tab IDs that cannot be deleted (e.g., 'default')
  editable?: boolean;  // Enable edit mode with rename/delete controls
  className?: string;
  noBorder?: boolean;
  compact?: boolean;  // Reduces padding and makes tabs only as wide as content
}

const TabBar: React.FC<TabBarProps> = ({
  tabs,
  onTabChange,
  onAddTab,
  onEditTab,
  onDeleteTab,
  onReorderTabs,
  maxTabs = 9,
  protectedTabIds = [],
  editable = false,
  className = '',
  noBorder = false,
  compact = false
}) => {
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [editingTabName, setEditingTabName] = useState('');
  const [deleteConfirmTab, setDeleteConfirmTab] = useState<{ id: string; name: string } | null>(null);
  const [draggedTabId, setDraggedTabId] = useState<string | null>(null);
  const [dragOverTabId, setDragOverTabId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Overflow handling (compact mode): the tab strip scrolls horizontally once the
  // tabs outgrow the bar, with chevrons that appear only when there is more to see.
  const updateScrollAffordances = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 1px slack absorbs sub-pixel widths, which would otherwise leave a chevron
    // enabled with nothing left to scroll to.
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < maxScroll - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollAffordances();
    const observer = new ResizeObserver(updateScrollAffordances);
    observer.observe(el);
    // Observe the inner row too, so adding/renaming a tab re-measures.
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [updateScrollAffordances, tabs.length]);

  // Keep the active tab visible when it changes (e.g. a new workspace is created
  // past the right edge, or a workspace is activated by shortcut).
  const currentTabId = tabs.find(t => t.current)?.id;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !currentTabId) return;
    const active = el.querySelector(`[data-tab-id="${CSS.escape(currentTabId)}"]`);
    active?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [currentTabId]);

  const scrollByStep = (direction: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(160, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  // Drag and drop handlers for tab reordering (only active in edit mode)
  const handleDragStart = (e: React.DragEvent, tabId: string) => {
    if (!editable) return;
    setDraggedTabId(tabId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', tabId);
  };

  const handleDragOver = (e: React.DragEvent, tabId: string) => {
    if (!editable || !draggedTabId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (tabId !== draggedTabId) {
      setDragOverTabId(tabId);
    }
  };

  const handleDragLeave = () => {
    if (!editable) return;
    setDragOverTabId(null);
  };

  const handleDrop = (e: React.DragEvent, dropTabId: string) => {
    if (!editable) return;
    e.preventDefault();
    if (!draggedTabId || draggedTabId === dropTabId || !onReorderTabs) {
      setDraggedTabId(null);
      setDragOverTabId(null);
      return;
    }

    const draggedIndex = tabs.findIndex(t => t.id === draggedTabId);
    const dropIndex = tabs.findIndex(t => t.id === dropTabId);

    if (draggedIndex === -1 || dropIndex === -1) {
      setDraggedTabId(null);
      setDragOverTabId(null);
      return;
    }

    // Reorder the tabs array
    const reordered = [...tabs];
    const [draggedTab] = reordered.splice(draggedIndex, 1);
    reordered.splice(dropIndex, 0, draggedTab);

    onReorderTabs(reordered);
    setDraggedTabId(null);
    setDragOverTabId(null);
  };

  const handleDragEnd = () => {
    if (!editable) return;
    setDraggedTabId(null);
    setDragOverTabId(null);
  };

  useEffect(() => {
    if (editingTabId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingTabId]);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedTab = tabs.find(tab => tab.label === e.target.value);
    if (selectedTab && !selectedTab.disabled && onTabChange) {
      onTabChange(selectedTab.id);
    }
  };

  const handleTabClick = (e: React.MouseEvent<HTMLAnchorElement>, tabId: string, disabled?: boolean) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    if (onTabChange) {
      e.preventDefault();
      onTabChange(tabId);
    }
  };

  const startEditing = (e: React.MouseEvent, tabId: string, currentName: string) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingTabId(tabId);
    setEditingTabName(currentName);
  };

  const finishEditing = () => {
    if (editingTabId && editingTabName.trim() && onEditTab) {
      onEditTab(editingTabId, editingTabName.trim());
    }
    setEditingTabId(null);
    setEditingTabName('');
  };

  const cancelEditing = () => {
    setEditingTabId(null);
    setEditingTabName('');
  };

  const handleDeleteClick = (e: React.MouseEvent, tabId: string, tabName: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteConfirmTab({ id: tabId, name: tabName });
  };

  const confirmDelete = () => {
    if (deleteConfirmTab && onDeleteTab) {
      onDeleteTab(deleteConfirmTab.id);
    }
    setDeleteConfirmTab(null);
  };

  const currentTab = tabs.find(tab => tab.current);

  return (
    <div className={className}>
      {/* Delete Confirmation Modal */}
      <InfoModal
        isOpen={deleteConfirmTab !== null}
        onClose={() => setDeleteConfirmTab(null)}
        title="Delete Workspace"
        maxWidth="sm"
        body={
          <p className="text-gray-600 dark:text-gray-300">
            Are you sure you want to delete <span className="font-semibold">"{deleteConfirmTab?.name}"</span>? All cards in this workspace will be moved to the trashcan for 30 days.
          </p>
        }
        buttons={[
          {
            label: 'Delete',
            onClick: confirmDelete,
            variant: 'danger',
          },
          {
            label: 'Cancel',
            onClick: () => setDeleteConfirmTab(null),
            variant: 'secondary',
          },
        ]}
      />

      <div className="flex sm:hidden border-b border-gray-300 dark:border-gray-600 bg-panel">
        {tabs.map((tab) => {
          const isCurrent = tab.current;
          return (
            <button
              key={tab.id}
              type="button"
              title={tab.required ? 'Manager-required workspace' : undefined}
              accessKey={tab.accessKey}
              onClick={() => !tab.disabled && onTabChange?.(tab.id)}
              disabled={tab.disabled}
              className={`flex-1 py-2.5 text-sm font-medium text-center relative ${
                tab.required
                  ? isCurrent
                    ? 'bg-blue-200 text-blue-950 dark:bg-blue-200 dark:text-blue-950'
                    : 'bg-blue-100 text-blue-900 hover:bg-blue-200 dark:bg-blue-100 dark:text-blue-900 dark:hover:bg-blue-200'
                  : isCurrent
                  ? 'text-cmp-tab-active-fg'
                  : tab.disabled
                  ? 'text-disabled cursor-not-allowed'
                  : 'text-cmp-tab-fg'
              }`}
            >
              <span className="inline-flex items-center justify-center gap-1.5">
                {tab.icon && <span>{tab.icon}</span>}
                {tab.label}
                {tab.badge !== undefined && (
                  <span className="ml-1 py-0.5 px-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                    {tab.badge}
                  </span>
                )}
              </span>
              {isCurrent && (
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-cmp-tab-edge" />
              )}
            </button>
          );
        })}
      </div>
      <div className={`hidden sm:flex items-stretch min-w-0 ${noBorder ? 'bg-transparent border-b border-gray-300/60 dark:border-gray-600/60' : 'bg-panel border-b border-gray-300 dark:border-gray-600 shadow-sm ring-1 ring-gray-200 dark:ring-gray-700'}`}>
        {compact && canScrollLeft && (
          <button
            type="button"
            onClick={() => scrollByStep(-1)}
            className="shrink-0 self-stretch px-1 text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100/75 dark:hover:bg-gray-700/75"
            title="Scroll tabs left"
            aria-label="Scroll tabs left"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
        )}
        <nav
          ref={scrollRef}
          aria-label="Tabs"
          onScroll={compact ? updateScrollAffordances : undefined}
          className={`min-w-0 flex-1 ${compact ? 'overflow-x-auto tabbar-scroll' : ''}`}
        >
        <div className={`isolate flex ${compact ? 'px-2 gap-1 w-max' : 'w-full divide-x divide-gray-200 dark:divide-gray-700'}`}>
          {tabs.map((tab, index) => {
            const isFirst = index === 0;
            const isLast = index === tabs.length - 1;
            const isCurrent = tab.current;
            const isDisabled = tab.disabled;
            const isEditing = editingTabId === tab.id;
            const isProtected = protectedTabIds.includes(tab.id);

            const isDragging = draggedTabId === tab.id;
            const isDragOver = dragOverTabId === tab.id;

            return (
              <div
                key={tab.id}
                data-tab-id={tab.id}
                className={`relative flex items-center ${compact ? 'shrink-0' : ''} ${isDragging ? 'opacity-50' : ''} ${isDragOver ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                draggable={editable && !isEditing}
                onDragStart={(e) => handleDragStart(e, tab.id)}
                onDragOver={(e) => handleDragOver(e, tab.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, tab.id)}
                onDragEnd={handleDragEnd}
              >
                {isEditing ? (
                  <div className={`flex items-center ${compact ? 'px-2 py-1 rounded-t-md mt-[3px]' : 'px-4 py-4'} bg-gray-100 dark:bg-gray-700`}>
                    <input
                      ref={inputRef}
                      type="text"
                      value={editingTabName}
                      onChange={(e) => setEditingTabName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          finishEditing();
                        } else if (e.key === 'Escape') {
                          cancelEditing();
                        }
                      }}
                      onBlur={finishEditing}
                      className="px-2 py-0.5 text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-panel text-heading w-24"
                    />
                  </div>
                ) : (
                  <a
                    href={tab.href || '#'}
                    title={tab.required ? 'Manager-required workspace' : undefined}
                    accessKey={tab.accessKey}
                    onClick={(e) => handleTabClick(e, tab.id, tab.disabled)}
                    aria-current={isCurrent ? 'page' : undefined}
                    aria-disabled={isDisabled}
                    className={`
                      group relative min-w-0 overflow-hidden text-center text-sm font-medium
                      ${compact ? 'px-3 py-1.5 rounded-t-md mt-[3px]' : 'flex-1 px-4 py-4'}
                      ${compact && isCurrent ? 'border-l border-t border-r border-gray-300 dark:border-gray-600' : ''}
                      ${compact && !isCurrent && !isDisabled ? 'border-l border-t border-r border-gray-300/50 dark:border-gray-600/50' : ''}
                      ${compact && !isCurrent && !isDisabled && !tab.required ? 'bg-gray-100/50 dark:bg-gray-700/50' : ''}
                      ${tab.required
                        ? isCurrent
                          ? 'bg-blue-200 text-blue-950 dark:bg-blue-200 dark:text-blue-950'
                          : 'bg-blue-100 text-blue-900 hover:bg-blue-200 dark:bg-blue-100 dark:text-blue-900 dark:hover:bg-blue-200'
                        : isCurrent
                        ? 'text-cmp-tab-active-fg bg-gray-100 dark:bg-gray-700'
                        : isDisabled
                        ? 'text-disabled cursor-not-allowed'
                        : 'text-muted hover:text-gray-700 dark:hover:text-gray-300'}
                      ${!isDisabled && !isCurrent && !tab.required ? 'hover:bg-gray-100/75 dark:hover:bg-gray-700/75' : ''} focus:z-10
                      ${editable && !isProtected ? 'pr-12' : ''}
                    `}
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      {tab.icon && (
                        <span className={`
                          ${tab.required
                            ? 'text-blue-700 dark:text-blue-700'
                            : isCurrent
                            ? 'text-indigo-500 dark:text-indigo-400'
                            : isDisabled
                            ? 'text-disabled'
                            : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}
                        `}>
                          {tab.icon}
                        </span>
                      )}
                      <span>{tab.label}</span>
                      {tab.badge !== undefined && (
                        <span className={`
                          ml-2 py-0.5 px-2 rounded-full text-xs font-medium
                          ${isCurrent
                            ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}
                        `}>
                          {tab.badge}
                        </span>
                      )}
                    </span>
                    {!compact && (
                      <span
                        aria-hidden="true"
                        className={`absolute inset-x-0 bottom-0 h-0.5 ${
                          isCurrent ? 'bg-cmp-tab-edge' : 'bg-transparent'
                        }`}
                      ></span>
                    )}
                    {/* Edit/Delete controls - inside the <a> so they live within pr-12 space */}
                    {editable && !isEditing && !isProtected && (
                      <span className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={(e) => startEditing(e, tab.id, tab.label)}
                          className="p-0.5 rounded hover:bg-blue-500 hover:text-white text-gray-400 dark:text-gray-500 transition-colors"
                          title="Rename"
                        >
                          <PencilIcon className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleDeleteClick(e, tab.id, tab.label)}
                          className="p-0.5 rounded hover:bg-red-500 hover:text-white text-gray-400 dark:text-gray-500 transition-colors"
                          title="Delete"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                  </a>
                )}
              </div>
            );
          })}
          {/* Add tab button - shown when under max tabs and onAddTab is provided */}
          {compact && onAddTab && tabs.length < maxTabs && (
            <button
              type="button"
              onClick={onAddTab}
              className="shrink-0 px-3 py-1.5 rounded-t-md mt-[3px] text-sm font-medium border-l border-t border-r border-green-400/50 dark:border-green-600/50 bg-green-100/50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 hover:border-green-400 dark:hover:border-green-600 focus:z-10 transition-colors"
              title="Add new workspace"
            >
              +
            </button>
          )}
        </div>
        </nav>
        {compact && canScrollRight && (
          <button
            type="button"
            onClick={() => scrollByStep(1)}
            className="shrink-0 self-stretch px-1 text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100/75 dark:hover:bg-gray-700/75"
            title="Scroll tabs right"
            aria-label="Scroll tabs right"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default TabBar;
